/**
 * OptimalTeamCard — Dense horizontal row for mobile optimal teams list.
 *
 * Layout:
 *   [sticky: rank / pts / Δ$] | scrollable → [C1][C2] | [×3?][×2] | [D1][D2][D3][D4]
 *
 * The left column (rank + pts + Δ$) is always visible while the user swipes
 * the badge section left/right. The parent container must have overflow-x-auto.
 */

import { Plus } from "lucide-react";
import { m } from "@/paraglide/messages";
import type {
  OptimalTeam,
  PredictedConstructor,
  PredictedDriver,
} from "../../lib/algorithms";
import { getTeamColor } from "../../lib/team-colors";

const CTOR_SHORT: Record<string, string> = {
  mclaren: "MCL",
  ferrari: "FER",
  "red-bull-racing": "RBR",
  "red-bull-racing-honda": "RBR",
  "red-bull-racing-rbpt": "RBR",
  mercedes: "MER",
  "aston-martin": "AM",
  alpine: "ALP",
  williams: "WIL",
  rb: "RB",
  "racing-bulls": "RB",
  haas: "HAS",
  sauber: "SAU",
  audi: "AUD",
  cadillac: "CAD",
  alphatauri: "AT",
  "alphatauri-honda": "AT",
  "alphatauri-rbpt": "AT",
  "alfa-romeo": "AR",
  "alfa-romeo-racing": "AR",
  "racing-point": "RP",
  renault: "REN",
};

function ctorShort(slug: string): string {
  return CTOR_SHORT[slug] ?? slug.slice(0, 3).toUpperCase();
}

interface OptimalTeamCardProps {
  team: OptimalTeam;
  driverMap: Map<string, PredictedDriver>;
  constructorMap: Map<string, PredictedConstructor>;
  selectedDriverSlugs: string[];
  selectedConstructorSlugs: string[];
  hasX3Chip: boolean;
  isPredictionMode: boolean;
  /** When provided, the card gets a "use this team" action. */
  onApplyTeam?: (team: OptimalTeam) => void;
}

export function OptimalTeamCard({
  team,
  driverMap,
  constructorMap,
  selectedDriverSlugs,
  selectedConstructorSlugs,
  hasX3Chip,
  isPredictionMode,
  onApplyTeam,
}: OptimalTeamCardProps) {
  const ctor1 = constructorMap.get(team.constructors[0]);
  const ctor2 = constructorMap.get(team.constructors[1]);
  const x2Driver = driverMap.get(team.x2DriverSlug);
  const x3Driver = team.x3DriverSlug ? driverMap.get(team.x3DriverSlug) : null;

  const boostedCodes = [team.x2DriverSlug, team.x3DriverSlug].filter(Boolean);
  const otherDrivers = team.drivers
    .filter((code) => !boostedCodes.includes(code))
    .map((code) => driverMap.get(code))
    .filter((d): d is PredictedDriver => d !== undefined);

  const ptsPositive = team.predictedBudgetChange >= 0;

  return (
    <div className="flex items-stretch">
      {/* ── Sticky left: rank / pts / Δ$ ─────────────────────────── */}
      <div className="sticky left-0 z-10 bg-card flex flex-col items-center justify-end shrink-0 w-[52px] pt-2 pb-2.5 border-r border-border/40">
        <span className="font-mono text-[10px] text-muted-foreground tabular-nums leading-none">
          {team.rank}
        </span>
        <span
          className={`font-mono text-sm font-bold tabular-nums leading-none mt-1 ${
            isPredictionMode ? "text-amber-500" : "text-foreground"
          }`}
        >
          {team.predictedPoints.toFixed(0)}
        </span>
        <span
          className={`font-mono text-xs font-bold tabular-nums leading-none mt-0.5 ${
            ptsPositive ? "text-success" : "text-destructive"
          }`}
        >
          {ptsPositive ? "+" : ""}
          {team.predictedBudgetChange.toFixed(1)}
        </span>
      </div>

      {/* ── Scrollable badge area ──────────────────────────────────── */}
      <div className="flex items-stretch gap-0.5 px-2 py-1.5">
        {/* Constructors */}
        <div className="flex gap-1 shrink-0">
          {ctor1 && (
            <RowBadge
              code={ctorShort(ctor1.slug)}
              teamColor={getTeamColor(ctor1.slug)}
              xPts={ctor1.predictedRacePoints}
              xPriceChange={ctor1.predictedPriceChange}
              isPredictionMode={isPredictionMode}
              isSelected={selectedConstructorSlugs.includes(ctor1.slug)}
            />
          )}
          {ctor2 && (
            <RowBadge
              code={ctorShort(ctor2.slug)}
              teamColor={getTeamColor(ctor2.slug)}
              xPts={ctor2.predictedRacePoints}
              xPriceChange={ctor2.predictedPriceChange}
              isPredictionMode={isPredictionMode}
              isSelected={selectedConstructorSlugs.includes(ctor2.slug)}
            />
          )}
        </div>

        {/* Separator */}
        <div className="w-px bg-border/40 self-stretch shrink-0 mx-1" />

        {/* Boost driver(s) */}
        <div className="flex gap-1 shrink-0">
          {hasX3Chip && x3Driver && (
            <RowBadge
              code={x3Driver.code}
              teamColor={getTeamColor(x3Driver.constructorSlug)}
              boost="×3"
              boostClass="text-amber-500"
              xPts={x3Driver.predictedRacePoints}
              xPriceChange={x3Driver.predictedPriceChange}
              isPredictionMode={isPredictionMode}
              isSelected={selectedDriverSlugs.includes(x3Driver.driverSlug)}
            />
          )}
          {x2Driver && (
            <RowBadge
              code={x2Driver.code}
              teamColor={getTeamColor(x2Driver.constructorSlug)}
              boost="×2"
              boostClass="text-secondary"
              xPts={x2Driver.predictedRacePoints}
              xPriceChange={x2Driver.predictedPriceChange}
              isPredictionMode={isPredictionMode}
              isSelected={selectedDriverSlugs.includes(x2Driver.driverSlug)}
            />
          )}
        </div>

        {/* Separator */}
        <div className="w-px bg-border/40 self-stretch shrink-0 mx-1" />

        {/* Regular drivers */}
        <div className="flex gap-1 shrink-0">
          {otherDrivers.map((driver) => (
            <RowBadge
              key={driver.driverSlug}
              code={driver.code}
              teamColor={getTeamColor(driver.constructorSlug)}
              xPts={driver.predictedRacePoints}
              xPriceChange={driver.predictedPriceChange}
              isPredictionMode={isPredictionMode}
              isSelected={selectedDriverSlugs.includes(driver.driverSlug)}
            />
          ))}
        </div>

        {/* Use this team */}
        {onApplyTeam && (
          <button
            type="button"
            onClick={() => onApplyTeam(team)}
            title={m.f1_myteam_apply()}
            aria-label={m.f1_myteam_apply()}
            className="self-center shrink-0 ml-1 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors min-h-[36px]"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RowBadge — compact vertical tile: CODE / pts / price
// ============================================================================

interface RowBadgeProps {
  code: string;
  teamColor: string;
  boost?: string;
  boostClass?: string;
  xPts?: number;
  xPriceChange?: number;
  isPredictionMode?: boolean;
  isSelected?: boolean;
}

function RowBadge({
  code,
  teamColor,
  boost,
  boostClass,
  xPts,
  xPriceChange,
  isPredictionMode,
  isSelected,
}: RowBadgeProps) {
  return (
    <div
      className={`rounded px-1.5 pt-1 pb-1 min-w-11 ${isSelected ? "ring-1 ring-primary/60" : ""}`}
      style={{
        borderLeft: `3px solid ${teamColor}`,
        backgroundColor: `${teamColor}18`,
      }}
    >
      {/* Code + optional boost prefix */}
      <div className="flex items-baseline gap-0.5 leading-none">
        {boost && (
          <span className={`text-[9px] font-bold leading-none ${boostClass}`}>
            {boost}
          </span>
        )}
        <span className="text-xs font-bold text-foreground leading-none">
          {code}
        </span>
      </div>
      {/* Pts */}
      {xPts !== undefined && (
        <div
          className={`font-mono text-xs leading-none mt-0.5 ${isPredictionMode ? "text-amber-400" : "text-foreground/60"}`}
        >
          {xPts.toFixed(1)}
        </div>
      )}
      {/* Price change */}
      {xPriceChange !== undefined && (
        <div
          className={`font-mono text-xs leading-none mt-0.5 ${xPriceChange >= 0 ? "text-success" : "text-destructive"}`}
        >
          {xPriceChange >= 0 ? "+" : ""}
          {xPriceChange.toFixed(2)}
        </div>
      )}
    </div>
  );
}
